/** Clips are session-scoped: a resolved clip is memoized in memory and nothing is written to disk. */
import { inject, Injectable, computed, signal } from '@angular/core';
import type { EncodedVideoPacketSource as PacketSource } from 'mediabunny';
import { WclFight } from '../wcl/wcl.models';
import { ClipAnchor } from '../capture/capture.models';
import { LoggerService } from '../../../shared/util-logging/logger-service';

// All wall-clock fields are unix-epoch milliseconds, so `report.startTime + fight.startTime` maps directly onto a segment.

export interface CaptureProfile {
  codec: 'vp9' | 'vp8';
  maxHeight: number;
  fps: number;
  bitrateBps: number;
}

export interface Segment {
  start: number;
  end: number;
  blob: Blob;
}

export interface ClipWindow {
  fromMs: number;
  toMs: number;
  key: string;
}

export interface ClipHandle {
  blob: Blob;
  startOffsetS: number;
  endOffsetS: number;
}

export interface ClipRoll {
  preMs: number;
  postMs: number;
}

export type DownloadOutcome = 'ok' | 'no-footage' | 'failed';

const DEFAULT_CAPTURE_PROFILE: CaptureProfile = {
  codec: 'vp9',
  maxHeight: 1080,
  fps: 30,
  bitrateBps: 4_000_000,
};

/** A single continuous recorder with `timeslice` yields header-less chunks the rolling buffer cannot trim or remux. */
const SEG_MS = 3_000;

/** Rolling-buffer retention: covers the longest fight plus WCL upload lag plus pre-roll. */
const BUFFER_MS = 12 * 60 * 1_000;

const POINT_CLIP_ROLL: ClipRoll = { preMs: 5_000, postMs: 5_000 };
const NO_CLIP_ROLL: ClipRoll = { preMs: 0, postMs: 0 };

/** Grace period before a downloaded clip's object URL is revoked, so the browser can read the blob. */
const DOWNLOAD_URL_TTL_MS = 10_000;

const POLL_COUNTDOWN_TICK_MS = 1_000;

@Injectable({ providedIn: 'root' })
export class LiveCaptureFeatureService {
  private readonly logger = inject(LoggerService);
  private readonly liveActive = signal(false);

  readonly isCapturing = signal(false);
  readonly isStarting = signal(false);
  readonly sourceLabel = signal('');
  readonly captureProfile = signal<CaptureProfile>(DEFAULT_CAPTURE_PROFILE);
  readonly captureError = signal<string | null>(null);

  /** Includes `isStarting` so a cancelled picker flips the bound value back, which is what unticks the switch the click ticked. */
  readonly recordToggleOn = computed(() => this.isCapturing() || this.isStarting());

  private stream: MediaStream | null = null;
  private readonly segments = signal<Segment[]>([]);
  private mimeType = 'video/webm';

  readonly liveEnabled = this.liveActive.asReadonly();
  readonly status = signal('');

  /** Either one discards state a page leave cannot recover, so callers check both through one flag. */
  readonly hasActiveSession = computed(() => this.liveEnabled() || this.isCapturing());

  private readonly pollDeadlineAt = signal<number | null>(null);
  private readonly pollCountdownNow = signal(Date.now());
  private pollCountdownTimer: ReturnType<typeof setInterval> | null = null;

  // Reads a ticking signal rather than routing through setStatus(), which clears pollDeadlineAt and would cancel the very countdown it renders.
  readonly statusText = computed(() => {
    const deadline = this.pollDeadlineAt();
    if (deadline == null) return this.status();
    const remainingS = Math.max(0, Math.ceil((deadline - this.pollCountdownNow()) / 1_000));
    return `Next update in ${remainingS}s`;
  });

  readonly open = signal(false);
  readonly handle = signal<ClipHandle | null>(null);
  readonly preparing = signal(false);
  readonly playbackFailed = signal(false);

  private readonly ctx = signal<{ reportCode: string; reportStartTime: number; fight: WclFight } | null>(null);
  private currentAnchor: ClipAnchor | null = null;
  /** Clips already cut this session, keyed `reportCode:fightId:windowKey`; keeps a window openable after the buffer rolls past it. */
  private readonly resolved = new Map<string, Promise<ClipHandle | null>>();

  setLive(on: boolean): void { this.liveActive.set(on); }

  setStatus(message: string): void {
    // Otherwise a running countdown's next tick would keep overwriting this message.
    this._stopPollCountdown();
    this.status.set(message);
  }

  scheduleNextPollIn(seconds: number): void {
    this.status.set('');
    this.pollDeadlineAt.set(Date.now() + seconds * 1_000);
    this.pollCountdownNow.set(Date.now());
    // Guards against stacking a second tick interval when back-to-back "skip" polls each reschedule.
    this.pollCountdownTimer ??= setInterval(() => { this.pollCountdownNow.set(Date.now()); }, POLL_COUNTDOWN_TICK_MS);
  }

  private _stopPollCountdown(): void {
    this.pollDeadlineAt.set(null);
    if (this.pollCountdownTimer != null) {
      clearInterval(this.pollCountdownTimer);
      this.pollCountdownTimer = null;
    }
  }

  async startRecording(profile: CaptureProfile = DEFAULT_CAPTURE_PROFILE): Promise<void> {
    if (this.isCapturing() || this.isStarting()) return;
    if (!this.displayMediaSupported()) {
      this.captureError.set('screen recording is not available in this browser');
      return;
    }
    this.captureError.set(null);
    this.isStarting.set(true);
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const [track] = stream.getVideoTracks();
      if (!track) throw new Error('Screen capture produced no video track.');
      await track.applyConstraints({ width: { max: 1920 }, height: { max: profile.maxHeight }, frameRate: { max: profile.fps } });
      this.stream = stream;
      this.captureProfile.set(profile);
      this.mimeType = this.mimeFor(profile);
      this.sourceLabel.set(track.label || 'your screen');
      // Sharing stopped from the browser UI (or the window closed) ends capture cleanly.
      track.addEventListener('ended', () => { this.stopRecording(); });
      this.isCapturing.set(true);
      this.cycleSegment();
    } catch (err) {
      // Tear down any half-started capture (an unsupported recorder throws after the stream opens) so the toggle never sticks on "Recording".
      this._releaseCapture(stream);
      // A dismissed picker is benign; a real failure (unsupported codec, denied by policy) surfaces so the user learns why nothing records.
      if (!this.isPickerDismissal(err)) this.captureError.set('recording could not start');
      this.logger.logWarn('LiveCaptureFeatureService.startRecording', err);
    } finally {
      this.isStarting.set(false);
    }
  }

  /** The buffer is kept so covered fights stay clip-able. */
  stopRecording(): void {
    this._releaseCapture(this.stream);
  }

  private _releaseCapture(stream: MediaStream | null): void {
    this.isCapturing.set(false);
    stream?.getTracks().forEach(track => { track.stop(); });
    this.stream = null;
    this.sourceLabel.set('');
  }

  /** Each segment is a complete WebM (own header + keyframe) so the remuxer can decode it on its own. */
  private cycleSegment(): void {
    if (!this.isCapturing() || !this.stream) return;
    const chunks: Blob[] = [];
    const recorder = new MediaRecorder(this.stream, { mimeType: this.mimeType, videoBitsPerSecond: this.captureProfile().bitrateBps });
    const start = Date.now();
    recorder.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
    // A runtime encoder failure would otherwise stall the buffer silently: surface it and tear the recording down.
    recorder.onerror = event => {
      this.logger.logWarn('LiveCaptureFeatureService.cycleSegment', event);
      this.captureError.set('recording stopped unexpectedly');
      this.stopRecording();
    };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: this.mimeType });
      const cutoff = Date.now() - BUFFER_MS;
      // Only a segment with footage counts toward clip coverage; a zero-byte blob cannot decode.
      if (blob.size) {
        const segment: Segment = { start, end: Date.now(), blob };
        this.segments.update(buffer => [...buffer.filter(existing => existing.end >= cutoff), segment]);
      }
      if (this.isCapturing()) this.cycleSegment();
    };
    recorder.start();
    setTimeout(() => { if (recorder.state === 'recording') recorder.stop(); }, SEG_MS);
  }

  prepare(reportCode: string, reportStartTime: number, fight: WclFight): void {
    this.ctx.set({ reportCode, reportStartTime, fight });
  }

  readonly clipReady = computed(() => {
    const ctx = this.ctx();
    if (!ctx) return false;
    const fightFrom = ctx.reportStartTime + ctx.fight.startTime;
    const fightTo = ctx.reportStartTime + ctx.fight.endTime;
    return this.segmentsCover(this.segments(), fightFrom, fightTo);
  });

  openClip(anchor: ClipAnchor): void {
    this.currentAnchor = anchor;
    this.open.set(true);
    this.playbackFailed.set(false);
    this.handle.set(null);
    const ctx = this.ctx();
    this.preparing.set(ctx != null);
    if (!ctx) {
      this.logger.logWarn(`LiveCaptureFeatureService.openClip ${anchor.key}`, 'no correlation context (report not resolved)');
      return;
    }
    void this.applyHandle(anchor, `${ctx.reportCode}:${ctx.fight.id}:${anchor.key}`, this.clipWindowFor(anchor, ctx));
  }

  download(): void {
    const anchor = this.currentAnchor;
    const handle = this.handle();
    if (!anchor || !handle) return;
    this.triggerDownload(handle.blob, `${anchor.key}.webm`);
  }

  async downloadFullPull(): Promise<DownloadOutcome> {
    const ctx = this.ctx();
    if (!ctx) return 'no-footage';
    const segments = this.selectSegments(this.segments(), this.fullPullWindow(ctx.reportStartTime, ctx.fight.startTime, ctx.fight.endTime));
    return this.saveSegments(segments.map(segment => segment.blob), 'full-pull.webm');
  }

  protected async saveSegments(blobs: Blob[], filename: string): Promise<DownloadOutcome> {
    if (!blobs.length) {
      this.logger.logWarn('LiveCaptureFeatureService.saveSegments', `no footage for ${filename}`);
      return 'no-footage';
    }
    try {
      this.triggerDownload(await this.remuxSegments(blobs), filename);
      return 'ok';
    } catch (err) {
      this.logger.logWarn('LiveCaptureFeatureService.saveSegments', err);
      return 'failed';
    }
  }

  onPlaybackError(): void {
    this.logger.logWarn('LiveCaptureFeatureService.onPlaybackError', this.currentAnchor?.key ?? '');
    this.playbackFailed.set(true);
  }

  close(): void { this.open.set(false); }

  /** Drop the flyover context and memoized clips (a new analysis); recording is independent and untouched. */
  clear(): void {
    this.open.set(false);
    this.handle.set(null);
    this.preparing.set(false);
    this.ctx.set(null);
    this.currentAnchor = null;
    this.resolved.clear();
    this.playbackFailed.set(false);
  }

  private clipWindowFor(anchor: ClipAnchor, ctx: { reportStartTime: number; fight: WclFight }): ClipWindow {
    const { reportStartTime, fight } = ctx;
    // A window plays its exact span; a point-in-time cast gets pre/post roll for context.
    const roll: ClipRoll = anchor.windowLengthS > 0 ? NO_CLIP_ROLL : POINT_CLIP_ROLL;
    return this.buildClipWindow(reportStartTime, fight.startTime, anchor, roll);
  }

  /** A second open supersedes the first, so a slow remux must never overwrite the newer clip. */
  private async applyHandle(anchor: ClipAnchor, key: string, window: ClipWindow): Promise<void> {
    const pending = this.resolved.get(key) ?? this.buildHandle(window);
    this.resolved.set(key, pending);
    try {
      const handle = await pending;
      if (this.currentAnchor !== anchor) return;
      this.handle.set(handle);
    } catch (err) {
      this.resolved.delete(key);
      if (this.currentAnchor !== anchor) return;
      this.logger.logWarn(`LiveCaptureFeatureService.applyHandle ${anchor.key}`, err);
      this.playbackFailed.set(true);
    }
    this.preparing.set(false);
  }

  /** The recorder-restart gaps are subtracted since the wall-clock span spans them but the remuxed timeline does not. */
  private async buildHandle(window: ClipWindow): Promise<ClipHandle | null> {
    const segments = this.selectSegments(this.segments(), window);
    if (!segments.length) return null;
    const startOffsetS = this.segmentSeekOffset(window, segments[0]);
    const loopSpanS = (window.toMs - window.fromMs - this.interSegmentGapMs(segments)) / 1000;
    return {
      blob: await this.remuxSegments(segments.map(segment => segment.blob)),
      startOffsetS,
      endOffsetS: startOffsetS + Math.max(0, loopSpanS),
    };
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    // Revoking synchronously can invalidate the URL before the browser reads the blob.
    setTimeout(() => { URL.revokeObjectURL(url); }, DOWNLOAD_URL_TTL_MS);
  }

  protected absoluteWindowStart(reportStartTime: number, fightStartTime: number, timeS: number): number {
    return reportStartTime + fightStartTime + timeS * 1000;
  }

  protected buildClipWindow(
    reportStartTime: number, fightStartTime: number, window: ClipAnchor, roll: ClipRoll,
  ): ClipWindow {
    const absStart = this.absoluteWindowStart(reportStartTime, fightStartTime, window.timeS);
    return {
      fromMs: absStart - roll.preMs,
      toMs: absStart + window.windowLengthS * 1000 + roll.postMs,
      key: window.key,
    };
  }

  protected fullPullWindow(reportStartTime: number, fightStartTime: number, fightEndTime: number): ClipWindow {
    return { fromMs: reportStartTime + fightStartTime, toMs: reportStartTime + fightEndTime, key: 'full-pull' };
  }

  /** Segments overlapping `[fromMs, toMs]`, sorted by start. Half-open on neither end (any touch counts). */
  protected selectSegments(segments: Segment[], window: ClipWindow): Segment[] {
    return segments
      .filter(segment => segment.end > window.fromMs && segment.start < window.toMs)
      .sort((a, b) => a.start - b.start);
  }

  /** The remuxed timeline re-bases to 0 at the first segment's start; never negative. */
  protected segmentSeekOffset(window: ClipWindow, firstSegment: { start: number } | undefined): number {
    if (!firstSegment) return 0;
    return Math.max(0, (window.fromMs - firstSegment.start) / 1000);
  }

  /** The remuxed timeline is gapless, so a loop length from a wall-clock span shrinks by this much to end on the same footage. */
  protected interSegmentGapMs(segments: { start: number; end: number }[]): number {
    let gaps = 0;
    let prev: { start: number; end: number } | undefined;
    for (const segment of segments) {
      if (prev) gaps += Math.max(0, segment.start - prev.end);
      prev = segment;
    }
    return gaps;
  }

  protected segmentsCover(segments: Segment[], fromMs: number, toMs: number): boolean {
    return segments.some(segment => segment.end > fromMs && segment.start < toMs);
  }

  private mimeFor(profile: CaptureProfile): string {
    const candidates = [`video/webm;codecs=${profile.codec}`, 'video/webm;codecs=vp8', 'video/webm'];
    return candidates.find(candidate => MediaRecorder.isTypeSupported(candidate)) ?? 'video/webm';
  }

  /** The picker-cancel and permission-deny paths both reject with a `NotAllowedError`. */
  private isPickerDismissal(err: unknown): boolean {
    return err instanceof DOMException && err.name === 'NotAllowedError';
  }

  // Insecure context or an unsupported browser leaves `getDisplayMedia` absent (the dom lib overpromises).
  private displayMediaSupported(): boolean {
    const mediaDevices = navigator.mediaDevices as MediaDevices | undefined;
    return typeof mediaDevices?.getDisplayMedia === 'function';
  }

  /** Each segment is a self-contained WebM whose clusters restart at 0, so a plain blob concat repeats the header and timeline and players read only the first segment's ~SEG_MS. */
  private async remuxSegments(blobs: Blob[]): Promise<Blob> {
    // A module-scope import would put the muxer in the landing bundle.
    const {
      BlobSource, BufferTarget, EncodedPacketSink, EncodedVideoPacketSource, Input, Output, WEBM, WebMOutputFormat,
    } = await import('mediabunny');
    const output = new Output({ format: new WebMOutputFormat(), target: new BufferTarget() });
    let source: PacketSource | null = null;
    // The decoder config only needs to ride the first packet; every segment shares one codec.
    let firstMeta: Parameters<PacketSource['add']>[1];
    let timeOffset = 0;
    for (const blob of blobs) {
      const track = await new Input({ formats: [WEBM], source: new BlobSource(blob) }).getPrimaryVideoTrack();
      if (!track) continue;
      if (!source) {
        source = new EncodedVideoPacketSource((await track.getCodec()) ?? 'vp8');
        output.addVideoTrack(source);
        await output.start();
        const config = await track.getDecoderConfig();
        firstMeta = config ? { decoderConfig: config } : undefined;
      }
      let segmentEnd = 0;
      for await (const packet of new EncodedPacketSink(track).packets()) {
        await source.add(packet.clone({ timestamp: packet.timestamp + timeOffset }), firstMeta);
        firstMeta = undefined;
        segmentEnd = Math.max(segmentEnd, packet.timestamp + packet.duration);
      }
      timeOffset += segmentEnd;
    }
    if (!source) throw new Error('no decodable video track in the buffered segments');
    await output.finalize();
    const buffer = output.target.buffer;
    if (!buffer) throw new Error('remux produced no output');
    return new Blob([buffer], { type: 'video/webm' });
  }
}
